import React, { useState } from 'react';
import { useLang } from '../../i18n';
import { Modal } from '../shared/Modal';
import { fmtDate } from '../../logic/dates';
import { isHandled } from '../../logic/items';

/**
 * ListDialog — the open list's "what now?" sheet, in two modes:
 *   • 'add'  — a new list cycle began (closing time) and someone is adding. Choose:
 *              move the open list to History & start a new one, or keep adding to it.
 *   • 'move' — the manual "📦 Move list to History" button.
 * Moving can keep unfinished items on the new list: ones noted today default ON (e.g.
 * notes jotted just before 8 PM), older ones default OFF (re-note them if still low).
 * Done + flagged items always go to History (flagged ones stay on Review to source).
 */
export function ListDialog({ mode, label, board, split, onMove, onSame, onClose }) {
  const { t, ln } = useLang();
  const [keepRecent, setKeepRecent] = useState(true);
  const [keepOlder, setKeepOlder] = useState(false);

  const done = split.done.filter(isHandled).length;
  const flagged = split.done.length - done;
  const notDone = split.recent.length + split.older.length;
  const kept = (keepRecent ? split.recent.length : 0) + (keepOlder ? split.older.length : 0);
  const moving = board.count - kept;
  const adding = mode === 'add';

  return (
    <Modal backdropClassName="do-list-back" className="do-list" onClose={onClose}
      label={adding ? t('list.dlgNewTitle', { date: fmtDate(board.curDay) }) : t('list.dlgMoveTitle')}>
      <div className="do-list__body">
        <h2 className="do-print__title">{adding ? t('list.dlgNewTitle', { date: fmtDate(board.curDay) }) : t('list.dlgMoveTitle')}</h2>
        <p className="do-list__open">{t('list.dlgOpen', { date: fmtDate(board.listDay), n: board.count })}</p>
        <div className="do-list__stats">
          <span className="do-list__stat do-list__stat--done">{t('list.statDone', { n: done })}</span>
          <span className="do-list__stat do-list__stat--todo">{t('list.statNotDone', { n: notDone })}</span>
          {flagged > 0 && <span className="do-list__stat do-list__stat--flag">{t('list.statFlagged', { n: flagged })}</span>}
        </div>
        {adding && label && <p className="do-list__adding">{t('list.dlgAdding', { name: ln(label) })}</p>}

        <div className="do-list__choice do-list__choice--primary">
          <button className="do-list__btn do-list__btn--primary" onClick={() => onMove({ keepRecent, keepOlder })}
            disabled={!adding && moving === 0}>
            {adding ? t('list.startNew') : t('list.moveConfirm', { n: moving })}
          </button>
          <p className="do-list__sub">{t('list.startNewSub')}</p>
          {split.recent.length > 0 && (
            <label className="do-list__keep">
              <input type="checkbox" checked={keepRecent} onChange={e => setKeepRecent(e.target.checked)} />
              {t('list.keepRecent', { n: split.recent.length })}
            </label>
          )}
          {split.older.length > 0 && (
            <label className="do-list__keep">
              <input type="checkbox" checked={keepOlder} onChange={e => setKeepOlder(e.target.checked)} />
              {t('list.keepOlder', { n: split.older.length })}
            </label>
          )}
        </div>

        {adding && (
          <div className="do-list__choice">
            <button className="do-list__btn" onClick={onSame}>{t('list.sameList')}</button>
            <p className="do-list__sub">{t('list.sameListSub')}</p>
          </div>
        )}
      </div>
      <div className="do-print__actions">
        <button className="do-modal__btn" onClick={onClose}>{t('common.cancel')}</button>
      </div>
    </Modal>
  );
}
